import axios from 'axios';
import { Modes } from '@/models/modes';
import notificationService from '@/services/notification.service';
import { opportunityService } from '@/services/opportunity.service';
import { tendersService } from '@/services/tenders.service';
import { NotificationSubjectTypes } from '@/util/constants/notificationSlugs';

const EVENTS_PAGE_SIZE = 10;

// The pages past the first, which the notifications page adds to `items` as the reader scrolls.
const initialMore = (firstPage = []) => ({
  nextPage: 2,
  exhausted: firstPage.length < EVENTS_PAGE_SIZE,
  loading: false,
  error: null,
});

const initialState = () => ({
  items: [],
  pagination: {
    page: 0,
    pageSize: EVENTS_PAGE_SIZE,
    totalCount: 0,
    totalPages: 0,
  },
  more: initialMore(),
  dialogItems: [],
  dialogPagination: {
    page: 0,
    pageSize: EVENTS_PAGE_SIZE,
    totalCount: 0,
    totalPages: 0,
  },
  unreadCount: 0,
  // subjectId -> mapped tender, or null when the fetch resolved but the tender no longer exists
  tendersById: {},
  // subjectId -> opportunity, or null when the fetch resolved but the opportunity no longer exists
  opportunitiesById: {},
  lastMarkAllAt: null,
  // Snapshot taken by MARK_ALL_READ so MARK_ALL_READ_ERROR can undo the optimistic write.
  markAllSnapshot: null,
  lastFetchedAt: null,
  status: {
    error: null,
    pending: false,
    resolved: false,
    rejected: false,
  },
  dialogStatus: {
    error: null,
    pending: false,
    resolved: false,
    rejected: false,
  },
  tendersStatus: {
    error: null,
    pending: false,
    resolved: false,
    rejected: false,
  },
  opportunitiesStatus: {
    error: null,
    pending: false,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

// A page that was in flight when mark-all ran must land already read.
const coerceReadState = (items, lastMarkAllAt) =>
  items.map((item) => (lastMarkAllAt && item.createdAt <= lastMarkAllAt ? { ...item, isRead: true } : item));

// Advance the delta baseline
const advanceLastFetchedAt = (state, items) => {
  if (!items || items.length === 0) return;

  const newest = items.reduce((max, item) => (item.createdAt > max ? item.createdAt : max), '');
  if (!state.lastFetchedAt || newest > state.lastFetchedAt) state.lastFetchedAt = newest;
};

const fetchFirstPage = ({ state, commit, dispatch }) => {
  commit('GET_EVENTS');
  return notificationService.getEvents({ page: 1, pageSize: state.pagination.pageSize }).then(
    (result) => {
      commit('GET_EVENTS_SUCCESS', result);
      return dispatch('fetchSubjectsForEvents', result.items);
    },
    (error) => {
      if (!axios.isCancel(error)) {
        commit('GET_EVENTS_ERROR', error);
      }
    },
  );
};

const actions = {
  // The counters run alongside the events request. Their failures must not touch `status`, which
  // belongs to that request: flipping it would drop the loading bar and show the empty state
  // ("you're all caught up") while the events are still on the wire. A stale count is the lesser evil.
  getUnreadCount({ commit }) {
    return notificationService.getUnreadCount().then(
      (result) => commit('SET_UNREAD_COUNT', result.count),
      () => {},
    );
  },
  getEventsCount({ commit }) {
    return notificationService.getEventsCount().then(
      (result) => commit('SET_EVENTS_COUNT', result.count),
      () => {},
    );
  },
  getEvents(context, payload) {
    const { fullReload = false } = payload || {};
    const { state, commit, dispatch } = context;
    if (state.status.pending) return Promise.resolve();

    const fullRefresh = () =>
      Promise.all([fetchFirstPage(context), dispatch('getEventsCount'), dispatch('getUnreadCount')]);

    if (fullReload || !state.lastFetchedAt) return fullRefresh();

    return notificationService
      .getEvents({ page: 1, pageSize: state.pagination.pageSize, since: state.lastFetchedAt })
      .then((result) => {
        if (!result.items || result.items.length === 0) return undefined;
        // A full page of deltas may be truncated; a prepend would leave a gap.
        if (result.items.length >= state.pagination.pageSize) return fullRefresh();

        commit('PREPEND_EVENTS', result.items);
        // PREPEND_EVENTS only adds what arrived here; reads made elsewhere (another tab, another
        // device) would otherwise never reach the badge until a full reload.
        return Promise.all([dispatch('fetchSubjectsForEvents', result.items), dispatch('getUnreadCount')]);
      })
      .catch(() => {}); // Background poll: fail silently; the next tick retries.
  },
  loadMoreEvents({ state, commit, dispatch }) {
    const more = state.more;
    if (more.loading || more.exhausted || state.status.pending) return Promise.resolve();

    commit('LOAD_MORE_EVENTS');
    return notificationService.getEvents({ page: more.nextPage, pageSize: state.pagination.pageSize }).then(
      (result) => {
        // A full refresh replaced the list, and this record, meanwhile: the page would leave a gap.
        if (state.more !== more) return undefined;

        commit('APPEND_EVENTS', result.items);
        return dispatch('fetchSubjectsForEvents', result.items);
      },
      (error) => {
        if (state.more === more) commit('LOAD_MORE_EVENTS_ERROR', error);
      },
    );
  },
  getDialogEvents({ state, commit, dispatch }, { page = 1 } = {}) {
    if (state.dialogStatus.pending) return Promise.resolve();

    commit('GET_DIALOG_EVENTS');
    return notificationService.getEvents({ page, pageSize: state.dialogPagination.pageSize }).then(
      (result) => {
        commit('GET_DIALOG_EVENTS_SUCCESS', result);
        return dispatch('fetchSubjectsForEvents', result.items);
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit('GET_DIALOG_EVENTS_ERROR', error);
        }
      },
    );
  },
  fetchSubjectsForEvents({ dispatch }, items) {
    return Promise.all([dispatch('fetchTendersForEvents', items), dispatch('fetchOpportunitiesForEvents', items)]);
  },
  fetchTendersForEvents({ state, commit }, items) {
    const tenderIds = [
      ...new Set(
        (items || [])
          .filter((item) => item.content && item.content.subjectType === NotificationSubjectTypes.TENDER)
          .map((item) => item.content.subjectId)
          .filter((id) => id && !(id in state.tendersById)),
      ),
    ];
    if (tenderIds.length === 0) return Promise.resolve();

    commit('GET_TENDERS');
    return tendersService
      .getTendersByMode({
        parameters: { tenderIds },
        mode: Modes.EXPLORE.apiCall,
        page: 1,
        sortingField: 'publishedDate',
        sortingOrder: 'desc',
        // Not cancelable: must not cross-cancel the shared search token used by the tender list pages.
        cancelable: false,
      })
      .then(
        ({ tenders }) => commit('GET_TENDERS_SUCCESS', { requestedIds: tenderIds, tenders }),
        (error) => commit('GET_TENDERS_ERROR', error),
      );
  },
  fetchOpportunitiesForEvents({ state, commit }, items) {
    const opportunityIds = [
      ...new Set(
        (items || [])
          .filter((item) => item.content && item.content.subjectType === NotificationSubjectTypes.OPPORTUNITY)
          .map((item) => item.content.subjectId)
          .filter((id) => id && !(id in state.opportunitiesById)),
      ),
    ];
    if (opportunityIds.length === 0) return Promise.resolve();

    commit('GET_OPPORTUNITIES');
    // No batch fetch by opportunity ids exists; pages hold at most 10 events so the fan-out stays small.
    return Promise.all(
      opportunityIds.map((id) =>
        opportunityService.getOpportunity({ id }).then(
          (opportunity) => ({ id, opportunity }),
          (error) => {
            // A deleted opportunity caches as null (same convention as tendersById). Any other
            // failure resolves to a marker instead of rejecting, so one bad id cannot discard the
            // whole batch; its id stays out of the cache and the next fetch retries just that one.
            if (error.response && error.response.status === 404) return { id, opportunity: null };
            return { id, error };
          },
        ),
      ),
    ).then((results) => {
      const fetched = results.filter((result) => !result.error);

      if (fetched.length === 0) {
        commit('GET_OPPORTUNITIES_ERROR', results[0].error);
        return;
      }
      commit('GET_OPPORTUNITIES_SUCCESS', fetched);
    });
  },
  resetDialogEvents({ commit }) {
    commit('RESET_DIALOG');
  },
  markAsRead({ state, commit }, id) {
    const item =
      state.items.find((notification) => notification.id === id) ||
      state.dialogItems.find((notification) => notification.id === id);
    if (!item || item.isRead) return Promise.resolve();

    commit('MARK_READ', id);
    return notificationService.markAsRead(id).catch(() => commit('MARK_READ_ERROR', id));
  },
  markAllAsRead({ state, commit, dispatch }) {
    commit('MARK_ALL_READ');
    return notificationService
      .markAllAsRead()
      .then(() => commit('MARK_ALL_READ_SUCCESS'))
      .catch(() => {
        // Undo the optimistic write first, or the refetch below lands and gets coerced back to read.
        commit('MARK_ALL_READ_ERROR');

        if (state.dialogPagination.page > 0) {
          dispatch('getDialogEvents', { page: state.dialogPagination.page });
        }
        // Full Reload: a delta since the baseline returns nothing and would leave the optimistic mark-all applied.
        return dispatch('getEvents', { fullReload: true });
      });
  },
};

const mutations = {
  GET_EVENTS(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  GET_EVENTS_SUCCESS(state, result) {
    advanceLastFetchedAt(state, result.items);
    state.items = coerceReadState(result.items, state.lastMarkAllAt);
    state.pagination = {
      ...state.pagination,
      page: result.page,
    };
    state.more = initialMore(result.items);
    state.status = {
      error: null,
      pending: false,
      resolved: true,
      rejected: false,
    };
  },
  GET_EVENTS_ERROR(state, error) {
    state.status = {
      error,
      pending: false,
      resolved: false,
      rejected: true,
    };
  },
  PREPEND_EVENTS(state, items) {
    advanceLastFetchedAt(state, items);

    const existingIds = new Set(state.items.map((item) => item.id));
    const newItems = coerceReadState(
      items.filter((item) => !existingIds.has(item.id)),
      state.lastMarkAllAt,
    );
    if (newItems.length === 0) return;

    // The delta is createdAt DESC, so prepending keeps the global order DESC.
    state.items = [...newItems, ...state.items];
    state.unreadCount += newItems.filter((item) => !item.isRead).length;

    const totalCount = state.pagination.totalCount + newItems.length;
    state.pagination = {
      ...state.pagination,
      totalCount,
      totalPages: Math.ceil(totalCount / state.pagination.pageSize),
    };
  },
  LOAD_MORE_EVENTS(state) {
    state.more.loading = true;
    state.more.error = null;
  },
  APPEND_EVENTS(state, items) {
    // Events that arrived since the list was loaded push the older ones down a page: the next
    // page can repeat some of them, but never skips any.
    const existingIds = new Set(state.items.map((item) => item.id));
    const olderItems = coerceReadState(
      items.filter((item) => !existingIds.has(item.id)),
      state.lastMarkAllAt,
    );
    state.items = [...state.items, ...olderItems];

    state.more.nextPage += 1;
    state.more.exhausted = items.length < state.pagination.pageSize;
    state.more.loading = false;
  },
  LOAD_MORE_EVENTS_ERROR(state, error) {
    state.more.loading = false;
    state.more.error = error;
  },
  GET_DIALOG_EVENTS(state) {
    state.dialogStatus = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  GET_DIALOG_EVENTS_SUCCESS(state, result) {
    state.dialogItems = coerceReadState(result.items, state.lastMarkAllAt);
    state.dialogPagination = {
      ...state.dialogPagination,
      page: result.page,
    };
    state.dialogStatus = {
      error: null,
      pending: false,
      resolved: true,
      rejected: false,
    };
  },
  GET_DIALOG_EVENTS_ERROR(state, error) {
    state.dialogStatus = {
      error,
      pending: false,
      resolved: false,
      rejected: true,
    };
  },
  RESET_DIALOG(state) {
    const initial = initialState();
    state.dialogItems = initial.dialogItems;
    state.dialogPagination = initial.dialogPagination;
    state.dialogStatus = initial.dialogStatus;
  },
  SET_UNREAD_COUNT(state, count) {
    state.unreadCount = count;
  },
  SET_EVENTS_COUNT(state, count) {
    state.pagination = {
      ...state.pagination,
      totalCount: count,
      totalPages: Math.ceil(count / state.pagination.pageSize),
    };
    state.dialogPagination = {
      ...state.dialogPagination,
      totalCount: count,
      totalPages: Math.ceil(count / state.dialogPagination.pageSize),
    };
  },
  GET_TENDERS(state) {
    state.tendersStatus = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  GET_TENDERS_SUCCESS(state, { requestedIds, tenders }) {
    const tendersById = {};
    requestedIds.forEach((id) => {
      tendersById[id] = null;
    });
    tenders.forEach((tender) => {
      tendersById[tender.id] = tender;
    });
    state.tendersById = { ...state.tendersById, ...tendersById };
    state.tendersStatus = {
      error: null,
      pending: false,
      resolved: true,
      rejected: false,
    };
  },
  GET_TENDERS_ERROR(state, error) {
    // Leave the cache untouched so the next events fetch retries.
    state.tendersStatus = {
      error,
      pending: false,
      resolved: false,
      rejected: true,
    };
  },
  GET_OPPORTUNITIES(state) {
    state.opportunitiesStatus = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  GET_OPPORTUNITIES_SUCCESS(state, results) {
    const opportunitiesById = {};
    results.forEach(({ id, opportunity }) => {
      opportunitiesById[id] = opportunity;
    });
    state.opportunitiesById = { ...state.opportunitiesById, ...opportunitiesById };
    state.opportunitiesStatus = {
      error: null,
      pending: false,
      resolved: true,
      rejected: false,
    };
  },
  GET_OPPORTUNITIES_ERROR(state, error) {
    // Leave the cache untouched so the next events fetch retries.
    state.opportunitiesStatus = {
      error,
      pending: false,
      resolved: false,
      rejected: true,
    };
  },
  MARK_READ(state, id) {
    // The same notification can live in both lists; adjust the count once.
    const matches = [...state.items, ...state.dialogItems].filter(
      (notification) => notification.id === id && !notification.isRead,
    );
    if (matches.length === 0) return;

    matches.forEach((item) => {
      item.isRead = true;
    });
    state.unreadCount = Math.max(0, state.unreadCount - 1);
  },
  MARK_READ_ERROR(state, id) {
    const matches = [...state.items, ...state.dialogItems].filter(
      (notification) => notification.id === id && notification.isRead,
    );
    if (matches.length === 0) return;

    matches.forEach((item) => {
      item.isRead = false;
    });
    state.unreadCount += 1;
  },
  MARK_ALL_READ(state) {
    state.markAllSnapshot = {
      lastMarkAllAt: state.lastMarkAllAt,
      unreadCount: state.unreadCount,
      unreadIds: [...state.items, ...state.dialogItems].filter((item) => !item.isRead).map((item) => item.id),
    };

    state.items = state.items.map((item) => ({ ...item, isRead: true }));
    state.dialogItems = state.dialogItems.map((item) => ({ ...item, isRead: true }));
    state.unreadCount = 0;
    // The newest createdAt already seen, not the client clock: a clock running fast would coerce
    // genuinely new events to read and they would never surface.
    state.lastMarkAllAt = state.lastFetchedAt;
  },
  MARK_ALL_READ_SUCCESS(state) {
    state.markAllSnapshot = null;
  },
  MARK_ALL_READ_ERROR(state) {
    const snapshot = state.markAllSnapshot;
    if (!snapshot) return;

    const wasUnread = new Set(snapshot.unreadIds);
    const restore = (items) => items.map((item) => (wasUnread.has(item.id) ? { ...item, isRead: false } : item));

    state.items = restore(state.items);
    state.dialogItems = restore(state.dialogItems);
    state.unreadCount = snapshot.unreadCount;
    state.lastMarkAllAt = snapshot.lastMarkAllAt;
    state.markAllSnapshot = null;
  },
};

const getters = {
  events: (state) => state.items,
  unreadCount: (state) => state.unreadCount,
  tenderById: (state) => (id) => state.tendersById[id],
  tendersPending: (state) => state.tendersStatus.pending,
  opportunityById: (state) => (id) => state.opportunitiesById[id],
  opportunitiesPending: (state) => state.opportunitiesStatus.pending,
  hasMoreEvents: (state) => state.items.length < state.pagination.totalCount,
  remainingEventsCount: (state) => Math.max(0, state.pagination.totalCount - state.items.length),
  canLoadMoreEvents: (state) => !state.more.exhausted && state.items.length < state.pagination.totalCount,
  loadingMoreEvents: (state) => state.more.loading,
  loadMoreEventsError: (state) => state.more.error,
  dialogEvents: (state) => state.dialogItems,
  dialogPage: (state) => state.dialogPagination.page,
  dialogTotalPages: (state) => state.dialogPagination.totalPages,
  dialogPending: (state) => state.dialogStatus.pending,

  // status
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
};

export const notifications = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
